import { DialogComponent } from '@theme/dialog';

/**
 * The menu "sticker sheet": a modal <dialog> that slides in from the left on every viewport.
 *
 * Everything a modal needs — showModal(), scroll lock, Escape, backdrop click, the closing
 * animation hand-off and focus return to the trigger — comes from DialogComponent (assets/dialog.js),
 * the same base the search modal and the bottom nav's More sheet use. This subclass only adds the
 * bits specific to a navigation sheet: mirroring `aria-expanded` onto the MENU sticker, putting
 * focus on the CLOSE sticker (which sits exactly where MENU was, so the sheet reads as the same
 * sticker flipping over), and opening itself when its block is selected in the theme editor.
 *
 * @extends {DialogComponent}
 */
class HeaderMenu extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:open', this.#onOpen);
    this.addEventListener('dialog:close', this.#onClose);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }

    // The tile photos are lazy (the sheet starts closed and display:none), so warm them on the
    // first sign of intent — a hover or focus on MENU — rather than at the moment the sheet opens.
    const trigger = this.trigger;
    if (trigger) {
      const once = { once: true, passive: true };
      trigger.addEventListener('pointerenter', this.#warmTiles, once);
      trigger.addEventListener('focus', this.#warmTiles, once);
      trigger.addEventListener('touchstart', this.#warmTiles, once);
    }
  }

  #warmed = false;

  #warmTiles = () => {
    if (this.#warmed) return;
    this.#warmed = true;
    for (const img of this.querySelectorAll('img[loading="lazy"]')) {
      img.loading = 'eager';
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#onOpen);
    this.removeEventListener('dialog:close', this.#onClose);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  /** @returns {HTMLElement | null} The MENU sticker that opens this sheet. */
  get trigger() {
    const id = this.refs.dialog?.id;
    if (!id) return null;
    const trigger = this.querySelector(`[aria-controls="${CSS.escape(id)}"]`);
    return trigger instanceof HTMLElement ? trigger : null;
  }

  #onOpen = () => {
    const trigger = this.trigger;
    trigger?.setAttribute('aria-expanded', 'true');

    // The CLOSE sticker lands where MENU sits. CSS derives the sheet inset from the page grid,
    // which is right when MENU is first in the bar; with the logo on the left it is not, so the
    // measured position wins whenever the trigger is on screen.
    const left = trigger?.getBoundingClientRect().left;
    if (left != null && left > 0) {
      this.refs.dialog?.style.setProperty('--hdr-sheet-inset', `${Math.round(left)}px`);
    }

    // Every open starts at the top of the sheet. Browsers keep a scroller's offset while it is
    // display:none, so a sheet closed halfway down would reopen halfway down. The reset can't live
    // in #onClose: by then dialog.close() has hidden the sheet, there is no box to scroll, and the
    // saved offset comes straight back on the next open. Here it lands in the same frame as
    // showModal(), before the first paint, so the old position never shows.
    const inner = this.refs.dialog?.querySelector('.hdr-sheet__inner');
    if (inner) inner.scrollTop = 0;

    // showModal() already lands focus on the first focusable descendant, which is the CLOSE
    // sticker by construction. Focusing it explicitly guards the markup order changing later.
    const close = this.refs.dialog?.querySelector('.hdr-sheet__close');
    if (close instanceof HTMLElement) close.focus({ preventScroll: true });
  };

  #onClose = () => {
    const trigger = this.trigger;
    trigger?.setAttribute('aria-expanded', 'false');
    this.refs.dialog?.style.removeProperty('--hdr-sheet-inset');

    // dialog.close() is meant to hand focus back to whatever had it before showModal(). Chromium
    // does; WebKit leaves it on <body> when the sheet was closed with Escape, so the next Tab
    // would start over from the top of the page. Put it back on the MENU sticker ourselves unless
    // something else already claimed focus (a link that was just followed, for instance).
    if (trigger && (document.activeElement === document.body || this.refs.dialog?.contains(document.activeElement))) {
      trigger.focus({ preventScroll: true });
    }
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) this.showDialog();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) this.closeDialog();
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
